import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MeetingTopic } from '../types/meeting.types.ts';
import {
  advanceAgendaSync,
  buildAgendaSegments,
  createInitialAgendaSync,
  resolveAgendaState,
  type AgendaResolvedState,
  type AgendaTimelineSync,
} from '../utils/agendaTimeline.ts';

interface UseMeetingAgendaOptions {
  topics: MeetingTopic[];
  isHost: boolean;
  isConnected: boolean;
  /** roomTimePolicy.startedAt veya Date.now() */
  fallbackStartedAt: number | null;
  broadcastAgendaTimeline?: (sync: AgendaTimelineSync) => void;
}

export function useMeetingAgenda({
  topics,
  isHost,
  isConnected,
  fallbackStartedAt,
  broadcastAgendaTimeline,
}: UseMeetingAgendaOptions) {
  const segments = useMemo(() => buildAgendaSegments(topics), [topics]);
  const [sync, setSync] = useState<AgendaTimelineSync | null>(null);
  const [resolved, setResolved] = useState<AgendaResolvedState>(() =>
    resolveAgendaState(createInitialAgendaSync(0), [])
  );
  const syncRef = useRef(sync);
  syncRef.current = sync;
  const broadcastRef = useRef(broadcastAgendaTimeline);
  broadcastRef.current = broadcastAgendaTimeline;

  const applyRemoteSync = useCallback((incoming: AgendaTimelineSync) => {
    setSync((prev) => {
      if (!prev) return incoming;
      // Daha yeni advancedAt veya daha ileri index kazanır
      const prevAdv = prev.advancedAt ?? 0;
      const nextAdv = incoming.advancedAt ?? 0;
      if (nextAdv > prevAdv) return incoming;
      if (nextAdv === prevAdv && incoming.currentIndex > prev.currentIndex) {
        return incoming;
      }
      if (incoming.startedAt < prev.startedAt && incoming.advancedAt == null && prev.advancedAt == null) {
        return incoming;
      }
      return prev;
    });
  }, []);

  // İlk sync: yalnızca host başlatır ve yayınlar; diğerleri socket bekler
  useEffect(() => {
    if (segments.length === 0) {
      setSync(null);
      return;
    }
    if (!isHost || !isConnected) return;
    if (syncRef.current) return;

    const startedAt = fallbackStartedAt ?? Date.now();
    const initial = createInitialAgendaSync(startedAt);
    setSync(initial);
    broadcastRef.current?.(initial);
  }, [segments.length, fallbackStartedAt, isHost, isConnected]);

  // Host bağlanınca / sync değişince duyur
  useEffect(() => {
    if (!isHost || !isConnected || !sync || segments.length === 0) return;
    broadcastRef.current?.(sync);
  }, [isHost, isConnected, sync, segments.length]);

  // 1sn tick
  useEffect(() => {
    if (!sync || segments.length === 0) {
      setResolved(resolveAgendaState(createInitialAgendaSync(0), []));
      return;
    }

    const tick = () => {
      setResolved(resolveAgendaState(sync, segments));
    };
    tick();
    const interval = window.setInterval(tick, 1000);
    return () => window.clearInterval(interval);
  }, [sync, segments]);

  const advanceAgenda = useCallback(() => {
    if (!isHost || !sync || segments.length === 0) return;
    const next = advanceAgendaSync(sync, segments);
    if (!next) return;
    setSync(next);
    broadcastRef.current?.(next);
  }, [isHost, sync, segments]);

  const hasAgenda = segments.length > 0;
  const canAdvance =
    isHost &&
    hasAgenda &&
    !!sync &&
    !resolved.isComplete &&
    resolved.index < segments.length - 1;

  return {
    hasAgenda,
    segments,
    sync,
    resolved,
    remainingSec: resolved.remainingSec,
    warnSoon: resolved.warnSoon,
    currentTopic: resolved.segment,
    nextTopic: resolved.nextSegment,
    isComplete: resolved.isComplete,
    canAdvance,
    advanceAgenda,
    applyRemoteSync,
  };
}
